import { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import useUserAccount from '../../../hooks/userAccountHook';
import { Insurer } from '../../../models/insurer/Insurer';

export interface SidebarState {
  isSidebarExpanded: boolean;
  expandedNavItems: Set<string>;
  expandedCategories: Record<string, boolean>;
  insurer?: Insurer;
}

export function useSidebarState() {
  const { density } = useTheme();
  const { user } = useUserAccount();
  
  const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(() => {
    return density === 'spacious' ? true : density === 'compact' ? false : true;
  });

  // Configurable default expanded items
  const getDefaultExpandedItems = (): Set<string> => {
    const defaults = ['/policies']; // Can be moved to config/constants
    const saved = localStorage.getItem('sidebar-expanded-items');
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return new Set([...defaults, ...parsed]);
      } catch (e) {
        console.warn('Failed to parse saved sidebar state');
      }
    }
    
    return new Set(defaults);
  };

  const [expandedNavItems, setExpandedNavItems] = useState<Set<string>>(getDefaultExpandedItems);
  
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    main: true,
    manage: true,
    system: true,
    financials: true
  });

  const [insurer, setInsurer] = useState<Insurer>();

  // Update sidebar expansion based on theme density
  useEffect(() => {
    if (density === 'spacious' && !isSidebarExpanded) {
      setIsSidebarExpanded(true);
    } else if (density === 'compact' && isSidebarExpanded) {
      setIsSidebarExpanded(false);
    }
  }, [density, isSidebarExpanded]);


  const toggleCategory = (category: string) => {
    if (!isSidebarExpanded) return;
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const toggleNavItem = (navPath: string) => {
    if (!isSidebarExpanded) return;
    setExpandedNavItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(navPath)) {
        newSet.delete(navPath);
      } else {
        newSet.add(navPath);
      }
      
      // Persist to localStorage (excluding defaults)
      const defaults = ['/policies'];
      const toSave = Array.from(newSet).filter(item => !defaults.includes(item));
      localStorage.setItem('sidebar-expanded-items', JSON.stringify(toSave));
      
      return newSet;
    });
  };

  const toggleSidebarExpansion = () => {
    setIsSidebarExpanded(prev => !prev);
  };

  return {
    isSidebarExpanded,
    expandedNavItems,
    expandedCategories,
    toggleCategory,
    toggleNavItem,
    toggleSidebarExpansion,
    setIsSidebarExpanded
  };
}
